import type { Party, PartyCreate, PartyDeletePreview, PartyType } from '@erp/shared';
import { Ban, Download, Pencil, Printer, RotateCcw, ShieldCheck, Trash2, Users } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { Button, confirmDialog, DataTable, type DataTableColumn, EmptyState, FormField, PhoneInput } from '../../components/ui/index.js';
import { MapPicker } from '../../components/ui/MapPicker.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { useBranchScope } from '../../lib/branches.js';
import { type CsvCell, downloadCsv, localDateStamp } from '../../lib/csv.js';
import { formatDate } from '../../lib/intl.js';
import { moduleEnabled } from '../../lib/modules.js';
import { useEnabledModules } from '../../lib/modulesStore.js';
import { formatMoney } from '../../lib/money.js';
import { fetchAllParties } from '../../lib/parties.js';
import { listPriceLists, type PriceList } from '../../lib/pricing.js';
import { BulkImportDialog } from '../common/BulkImportDialog.js';

import { PartiesListPrint } from './PartiesListPrint.js';

const PARTY_TYPES: PartyType[] = ['customer', 'vendor', 'both'];

// Fallback label for a delete-preview count key with no explicit i18n string
// (e.g. the backend adds a new referenced table): "salesOrders" -> "Sales orders".
function humanizeKey(key: string): string {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

const PARTIES_CSV_TEMPLATE = `code,display_name,party_type,email,phone,tax_id,payment_terms_days,credit_limit,credit_currency_code
CUST-001,Acme Corporation,customer,,+964-750-0100,IQ-12345,30,10000,IQD
VEND-001,Office Supplies Co,vendor,ap@supplies.test,+964-770-0200,,30,,
`;

export function PartiesPage({ side }: { side?: 'customer' | 'vendor' } = {}): JSX.Element {
  const { t, i18n } = useTranslation('parties');
  const { t: tc } = useTranslation();
  const canWriteAll = usePermission('parties.write');
  const canWriteCustomers = usePermission('customers.write');
  // The narrow client-book permission counts only on the CUSTOMER side — on
  // the suppliers page its holder is read-only, matching the API's scoping.
  const canWrite = canWriteAll || (canWriteCustomers && side !== 'vendor');
  const [parties, setParties] = useState<Party[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Party | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [creatorFilter, setCreatorFilter] = useState('');
  // '' = all · 'normal' = no price list · 'pending' = awaiting approval · else a price-list id.
  const [typeFilter, setTypeFilter] = useState('');
  const [debtorsOnly, setDebtorsOnly] = useState(false);
  // '' = every branch · 'shared' = customers of every branch · else a warehouse id.
  const [branchFilter, setBranchFilter] = useState('');
  // Which row's manager toggle is in flight (hooks must sit above any early return).
  const [managerBusy, setManagerBusy] = useState<string | null>(null);
  // Pages still to come — the table is usable while the tail streams in.
  const [loadingMore, setLoadingMore] = useState(false);
  // Guards a paged load against a newer one (Customers↔Suppliers, or a reload
  // after a save) overtaking it and interleaving two different books.
  const loadSeq = useRef(0);
  // The rows on screen, handed to the print paper; a fresh key re-prints.
  const [printJob, setPrintJob] = useState<{ key: number; rows: Party[]; filters: string[] } | null>(null);

  const reload = async (): Promise<void> => {
    // `metrics` asks the API to resolve the customer type (price list) + per-currency debts.
    // `portal` adds each customer's app-login + manager state (a cross-database
    // read), so it is asked for only on the customers list.
    //
    // One request stopped at the 500th name — the server's per-request cap, not
    // the size of a customer book. A shop with thousands of customers was shown
    // a fraction of them; everyone further down the alphabet, debts and all, was
    // missing from the table and from everything that reads its rows (the search
    // box, the account-type filter, the "with debts" tick). `fetchAllParties`
    // pages to the end and hands each page over as it lands, so the first 500
    // still render immediately.
    const seq = ++loadSeq.current;
    setLoadingMore(true);
    try {
      const all = await fetchAllParties<Party>(
        {
          metrics: 1,
          ...(side === 'customer' ? { portal: 1 } : {}),
          ...(side ? { partyType: side } : {}),
        },
        (soFar) => {
          if (seq !== loadSeq.current) return false; // superseded — stop paging
          setParties(soFar);
          return true;
        },
      );
      if (seq !== loadSeq.current) return;
      setParties(all);
    } finally {
      if (seq === loadSeq.current) setLoadingMore(false);
    }
  };

  useEffect(() => {
    setParties(null);
    setCreatorFilter('');
    setTypeFilter('');
    setDebtorsOnly(false);
    setBranchFilter('');
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [side]);

  // Distinct creators present (for the admin "added by" filter) + filtered rows.
  const creators = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of parties ?? []) if (p.createdByUserId) m.set(p.createdByUserId, p.createdByName ?? p.createdByUserId);
    return [...m.entries()];
  }, [parties]);
  // Home branches actually present — the Branch column and filter appear once a
  // customer has one; until then every customer is shared and the column is noise.
  const branchesInUse = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of parties ?? []) if (p.warehouseId) m.set(p.warehouseId, p.warehouseName ?? p.warehouseId);
    return [...m.entries()];
  }, [parties]);
  const showBranch = side !== 'vendor' && branchesInUse.length > 0;
  // Distinct account types (price lists) actually present in the loaded rows.
  const accountTypes = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of parties ?? []) if (p.priceListId && p.priceListName) m.set(p.priceListId, p.priceListName);
    return [...m.entries()];
  }, [parties]);
  const visible = useMemo(
    () => (parties ?? []).filter((p) => {
      if (creatorFilter && p.createdByUserId !== creatorFilter) return false;
      if (debtorsOnly && !(p.debts && p.debts.length > 0)) return false;
      if (branchFilter === 'shared' && p.warehouseId) return false;
      if (branchFilter && branchFilter !== 'shared' && p.warehouseId !== branchFilter) return false;
      if (typeFilter === 'normal') return !p.priceListId && !p.requestedPriceListId;
      if (typeFilter === 'pending') return !p.priceListId && !!p.requestedPriceListId;
      if (typeFilter) return p.priceListId === typeFilter;
      return true;
    }),
    [parties, creatorFilter, typeFilter, debtorsOnly, branchFilter],
  );
  const isVendor = side === 'vendor';

  if (!parties) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const onDeactivate = async (p: Party): Promise<void> => {
    if (!(await confirmDialog({ message: t('actions.confirmDeactivate', { code: p.code, name: p.displayName }), tone: 'danger' }))) return;
    try {
      await api().delete(`/parties/${p.id}`);
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const onReactivate = async (p: Party): Promise<void> => {
    if (!(await confirmDialog({ message: t('actions.confirmReactivate', { code: p.code, name: p.displayName }) }))) return;
    try {
      await api().patch(`/parties/${p.id}`, { isActive: true });
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  // Irreversible hard-delete: purge the party + every referencing record.
  // Only reachable from the Delete action, which is shown ONLY while the party
  // is inactive (deactivate-first). We fetch a read-only preview of what will be
  // wiped, list the non-zero counts in a danger dialog, then hit the purge route.
  const onPurge = async (p: Party): Promise<void> => {
    let preview: PartyDeletePreview;
    try {
      const res = await api().get<PartyDeletePreview>(`/parties/${p.id}/delete-preview`);
      preview = res.data;
    } catch {
      toast.error(tc('errors:server'));
      return;
    }

    // Per-table counts come from `preview.counts`; render only the non-zero rows.
    const counts = (preview.counts ?? {}) as Record<string, number>;
    const rows: string[] = [];
    for (const [key, value] of Object.entries(counts)) {
      if (!value) continue;
      const label = t(`delete.counts.${key}`, { defaultValue: humanizeKey(key) });
      rows.push(`• ${label}: ${value}`);
    }
    if (preview.portalLogin) rows.push(`• ${t('delete.portalLogin')}`);
    if (preview.cashbackBalance != null && Number(preview.cashbackBalance) !== 0) {
      rows.push(`• ${t('delete.cashback')}: ${preview.cashbackBalance}`);
    }
    if (preview.outstandingDebts && preview.outstandingDebts.length > 0) {
      const debts = preview.outstandingDebts
        .map((d) => formatMoney(d.amount, { locale: i18n.language, currency: d.currencyCode }))
        .join(' · ');
      rows.push(`• ${t('delete.outstanding')}: ${debts}`);
    }

    const message = [
      t('delete.intro', { code: p.code, name: p.displayName }),
      ...(rows.length ? ['', t('delete.willRemove'), ...rows] : []),
      '',
      t('delete.warning'),
    ].join('\n');

    if (!(await confirmDialog({
      title: t('actions.deleteTitle', { name: p.displayName }),
      message,
      tone: 'danger',
      confirmLabel: t('actions.deleteForever'),
    }))) return;

    try {
      await api().delete(`/parties/${p.id}/purge`);
      toast.success(t('delete.done', { code: p.code, name: p.displayName }));
      await reload();
    } catch (err) {
      // Problems.conflict puts the specific code in cause.code ('party_active');
      // the top-level code is the generic 'conflict'.
      const d = (err as { response?: { data?: { code?: string; cause?: { code?: string } } } }).response?.data;
      if ((d?.cause?.code ?? d?.code) === 'party_active') toast.error(t('errors.stillActive'));
      else toast.error(tc('errors:server'));
    }
  };

  /**
   * Grant/revoke the app-manager area straight from the list.
   *
   * Confirmed on the way IN only — it hands someone who is not a staff user
   * sight of every cost price and order; revoking needs no ceremony. Requires a
   * portal login to exist first, which the column enforces by not offering the
   * control at all without one.
   */
  const toggleManager = async (p: Party): Promise<void> => {
    const next = !p.isAppManager;
    if (next && !(await confirmDialog({ message: t('portalAccess.manager.confirm') }))) return;
    setManagerBusy(p.id);
    try {
      await api().put(`/parties/${p.id}/portal-access/manager`, { isAppManager: next });
      // Patch in place: a full reload would lose the operator's scroll position
      // and filters halfway down a long customer book.
      setParties((list) => (list ?? []).map((x) => (x.id === p.id ? { ...x, isAppManager: next } : x)));
      toast.success(next ? t('portalAccess.manager.grantedToast') : t('portalAccess.manager.revokedToast'));
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setManagerBusy(null);
    }
  };

  const openEdit = (p: Party): void => {
    setShowCreate(false);
    setEditing(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // The filters in force, in words — printed on the paper so a list of 40
  // "with debts at دهوك 2" is never mistaken for the whole book.
  const filterLabels = (query: string): string[] => {
    const out: string[] = [];
    if (typeFilter) {
      const name = typeFilter === 'normal'
        ? t('list.typeNormal', { defaultValue: 'Normal' })
        : typeFilter === 'pending'
          ? t('list.filterTypePending', { defaultValue: 'Pending approval' })
          : accountTypes.find(([id]) => id === typeFilter)?.[1] ?? '';
      out.push(`${t('list.columns.accountType', { defaultValue: 'Account type' })}: ${name}`);
    }
    if (debtorsOnly) out.push(isVendor ? t('list.filterOwed', { defaultValue: 'With balances' }) : t('list.filterDebtors', { defaultValue: 'With debts' }));
    if (branchFilter) {
      const name = branchFilter === 'shared' ? t('branch.sharedShort') : branchesInUse.find(([id]) => id === branchFilter)?.[1] ?? '';
      out.push(`${t('branch.label')}: ${name}`);
    }
    if (creatorFilter) out.push(`${t('list.columns.addedBy', { defaultValue: 'Added by' })}: ${creators.find(([id]) => id === creatorFilter)?.[1] ?? ''}`);
    if (query) out.push(`${t('listPrint.search', { defaultValue: 'Search' })}: “${query}”`);
    return out;
  };

  // The rows on screen as a spreadsheet: one column per debt currency, plain
  // numbers, so the sheet can sum what the book owes.
  const exportCsv = (rows: Party[]): void => {
    const ccys = [...new Set(rows.flatMap((p) => (p.debts ?? []).map((d) => d.currencyCode)))].sort();
    const debtLabel = isVendor ? t('list.columns.owed', { defaultValue: 'Owed' }) : t('list.columns.debts', { defaultValue: 'Debts' });
    const isCustomer = side === 'customer';
    const header: CsvCell[] = [
      '#',
      t('list.columns.code'),
      t('list.columns.name'),
      ...(!side ? [t('list.columns.type')] : []),
      t('list.columns.phone', { defaultValue: 'Phone' }),
      ...(isCustomer ? [t('list.columns.accountType', { defaultValue: 'Account type' })] : [t('list.columns.email')]),
      ...(showBranch ? [t('branch.label')] : []),
      ...(isCustomer ? [t('list.columns.portal', { defaultValue: 'App login' })] : []),
      ...(side ? ccys.map((c) => `${debtLabel} (${c})`) : []),
      t('list.columns.terms'),
      t('list.columns.addedBy', { defaultValue: 'Added by' }),
      t('list.columns.status'),
      t('list.columns.created', { defaultValue: 'Created' }),
    ];
    const body = rows.map((p, i): CsvCell[] => [
      i + 1,
      { text: p.code },
      p.displayName,
      ...(!side ? [t(`type.${p.partyType}`)] : []),
      { text: p.phone },
      ...(isCustomer
        ? [p.priceListId && p.priceListName
            ? p.priceListName
            : p.requestedPriceListId
              ? `${p.priceListName ?? ''} · ${t('list.typePending', { defaultValue: 'pending' })}`
              : t('list.typeNormal', { defaultValue: 'Normal' })]
        : [p.email ?? '']),
      ...(showBranch ? [p.warehouseId ? (p.warehouseName ?? '') : t('branch.sharedShort')] : []),
      ...(isCustomer ? [p.portalEnabled ? t('list.portalOn', { defaultValue: 'Yes' }) : ''] : []),
      ...(side ? ccys.map((c) => {
        const d = (p.debts ?? []).find((x) => x.currencyCode === c);
        return d ? Number(d.amount) : null;
      }) : []),
      p.paymentTermsDays,
      p.createdByName ?? '',
      t(`status.${p.isActive ? 'active' : 'inactive'}`),
      p.createdAt ? localDateStamp(new Date(p.createdAt)) : '',
    ]);
    downloadCsv(`${isVendor ? 'suppliers' : isCustomer ? 'customers' : 'parties'}-${localDateStamp()}.csv`, [header, ...body]);
  };

  const columns: DataTableColumn<Party>[] = [
    {
      id: 'no',
      header: t('list.columns.no', { defaultValue: '#' }),
      headClassName: 'w-12',
      cellClassName: 'text-fg-muted text-xs tabular-nums',
      cell: (_p, n) => n,
    },
    {
      id: 'code',
      header: t('list.columns.code'),
      sortValue: (p) => p.code,
      searchValue: (p) => p.code,
      cellClassName: 'font-mono text-xs',
      cell: (p) => p.code,
    },
    {
      id: 'name',
      header: t('list.columns.name'),
      sortValue: (p) => p.displayName,
      searchValue: (p) => p.displayName,
      cellClassName: 'font-medium',
      cell: (p) => (
        <Link
          to={`${side === 'vendor' ? '/purchasing/suppliers' : '/sales/parties'}/${p.id}`}
          className="text-fg underline-offset-4 hover:text-brand hover:underline"
        >
          {p.displayName}
        </Link>
      ),
    },
    // On a side-filtered page (Customers / Suppliers) the type badge is pure noise.
    ...(!side
      ? [{
          id: 'type',
          header: t('list.columns.type'),
          sortValue: (p: Party) => t(`type.${p.partyType}`),
          searchValue: (p: Party) => t(`type.${p.partyType}`),
          cell: (p: Party) => (
            <Badge
              variant={p.partyType === 'vendor' ? 'warning' : p.partyType === 'both' ? 'info' : 'success'}
            >
              {t(`type.${p.partyType}`)}
            </Badge>
          ),
        }]
      : []),
    ...(showBranch
      ? [{
          id: 'branch',
          header: t('branch.label'),
          sortValue: (p: Party) => p.warehouseName ?? '',
          searchValue: (p: Party) => p.warehouseName ?? t('branch.sharedShort'),
          cell: (p: Party) =>
            p.warehouseId ? (
              <Badge variant="info">{p.warehouseName ?? '—'}</Badge>
            ) : (
              <span className="text-xs text-fg-muted">{t('branch.sharedShort')}</span>
            ),
        }]
      : []),
    {
      id: 'phone',
      header: t('list.columns.phone', { defaultValue: 'Phone' }),
      sortValue: (p) => p.phone ?? '',
      searchValue: (p) => p.phone ?? '',
      cellClassName: 'font-mono text-xs text-fg-muted',
      cell: (p) => <span dir="ltr">{p.phone ?? '—'}</span>,
    },
    // Customers: the account type (price list) they buy at + what they owe us.
    ...(side === 'customer'
      ? [
          {
            id: 'accountType',
            header: t('list.columns.accountType', { defaultValue: 'Account type' }),
            sortValue: (p: Party) => p.priceListName ?? '',
            searchValue: (p: Party) => p.priceListName ?? '',
            cell: (p: Party) =>
              p.priceListId && p.priceListName ? (
                <Badge variant="info">{p.priceListName}</Badge>
              ) : p.requestedPriceListId ? (
                <Badge variant="warning">{p.priceListName ?? ''} · {t('list.typePending', { defaultValue: 'pending' })}</Badge>
              ) : (
                <span className="text-xs text-fg-muted">{t('list.typeNormal', { defaultValue: 'Normal' })}</span>
              ),
          },
          {
            // Can this customer sign in to the app at all? Manager access is
            // meaningless without it, so the two read together.
            id: 'portal',
            header: t('list.columns.portal', { defaultValue: 'App login' }),
            sortValue: (p: Party) => (p.portalEnabled ? 1 : 0),
            cell: (p: Party) =>
              p.portalEnabled ? (
                <Badge variant="success">{t('list.portalOn', { defaultValue: 'Yes' })}</Badge>
              ) : (
                <span className="text-xs text-fg-muted">—</span>
              ),
          },
          {
            id: 'appManager',
            header: t('portalAccess.manager.label'),
            sortValue: (p: Party) => (p.isAppManager ? 2 : p.portalEnabled ? 1 : 0),
            cell: (p: Party) => {
              // No login ⇒ nothing to grant. Say so rather than offering a
              // control whose every press would 422.
              if (!p.portalEnabled) return <span className="text-xs text-fg-muted">—</span>;
              if (!canWrite) {
                return p.isAppManager
                  ? <Badge variant="success">{t('portalAccess.manager.on')}</Badge>
                  : <span className="text-xs text-fg-muted">—</span>;
              }
              return (
                <Button
                  variant={p.isAppManager ? 'secondary' : 'ghost'}
                  size="sm"
                  loading={managerBusy === p.id}
                  onClick={() => void toggleManager(p)}
                  title={p.isAppManager ? t('portalAccess.manager.revoke') : t('portalAccess.manager.grant')}
                >
                  <ShieldCheck className={`h-3.5 w-3.5 ${p.isAppManager ? 'text-success' : 'text-fg-subtle'}`} aria-hidden />
                  <span className="ms-1">{p.isAppManager ? t('portalAccess.manager.on') : t('portalAccess.manager.grant')}</span>
                </Button>
              );
            },
          },
          {
            id: 'debts',
            header: t('list.columns.debts', { defaultValue: 'Debts' }),
            align: 'end' as const,
            sortValue: (p: Party) => Math.max(0, ...(p.debts ?? []).map((d) => Number(d.amount))),
            cellClassName: 'font-mono tabular-nums text-xs',
            cell: (p: Party) =>
              p.debts && p.debts.length > 0 ? (
                <span className="font-semibold text-danger" dir="ltr">
                  {p.debts.map((d) => formatMoney(d.amount, { locale: i18n.language, currency: d.currencyCode })).join(' · ')}
                </span>
              ) : (
                <span className="text-fg-muted">—</span>
              ),
          },
        ]
      : side === 'vendor'
      ? [
          {
            id: 'email',
            header: t('list.columns.email'),
            sortValue: (p: Party) => p.email ?? '',
            searchValue: (p: Party) => p.email ?? '',
            cellClassName: 'text-fg-muted',
            cell: (p: Party) => p.email ?? '—',
          },
          // Suppliers: AP outstanding — what WE owe them (approved/unpaid bills).
          {
            id: 'owed',
            header: t('list.columns.owed', { defaultValue: 'Owed' }),
            align: 'end' as const,
            sortValue: (p: Party) => Math.max(0, ...(p.debts ?? []).map((d) => Number(d.amount))),
            cellClassName: 'font-mono tabular-nums text-xs',
            cell: (p: Party) =>
              p.debts && p.debts.length > 0 ? (
                <span className="font-semibold text-danger" dir="ltr">
                  {p.debts.map((d) => formatMoney(d.amount, { locale: i18n.language, currency: d.currencyCode })).join(' · ')}
                </span>
              ) : (
                <span className="text-fg-muted">—</span>
              ),
          },
        ]
      : [{
          id: 'email',
          header: t('list.columns.email'),
          sortValue: (p: Party) => p.email ?? '',
          searchValue: (p: Party) => p.email ?? '',
          cellClassName: 'text-fg-muted',
          cell: (p: Party) => p.email ?? '—',
        }]),
    {
      id: 'terms',
      header: t('list.columns.terms'),
      align: 'end',
      sortValue: (p) => Number(p.paymentTermsDays),
      cellClassName: 'font-mono tabular-nums',
      cell: (p) => p.paymentTermsDays,
    },
    {
      id: 'addedBy',
      header: t('list.columns.addedBy', { defaultValue: 'Added by' }),
      sortValue: (p) => p.createdByName ?? '',
      searchValue: (p) => p.createdByName ?? '',
      cellClassName: 'text-fg-muted text-xs',
      cell: (p) => p.createdByName ?? '—',
    },
    {
      id: 'status',
      header: t('list.columns.status'),
      sortValue: (p) => (p.isActive ? 1 : 0),
      searchValue: (p) => t(`status.${p.isActive ? 'active' : 'inactive'}`),
      cell: (p) => (
        <Badge variant={p.isActive ? 'success' : 'secondary'}>
          {t(`status.${p.isActive ? 'active' : 'inactive'}`)}
        </Badge>
      ),
    },
    {
      id: 'createdAt',
      header: t('list.columns.created', { defaultValue: 'Created' }),
      sortValue: (p) => p.createdAt,
      cellClassName: 'whitespace-nowrap text-fg-muted text-xs',
      cell: (p) => formatDate(p.createdAt, { locale: i18n.language }),
    },
    ...(canWrite
      ? [
          {
            id: 'actions',
            header: '',
            align: 'end' as const,
            headClassName: 'w-44',
            cell: (p: Party) => (
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEdit(p)}
                  aria-label={`${t('actions.edit')} ${p.code}`}
                  title={t('actions.edit')}
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </Button>
                {p.isActive ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void onDeactivate(p)}
                    aria-label={`${t('actions.deactivate')} ${p.code}`}
                    title={t('actions.deactivate')}
                  >
                    <Ban className="h-4 w-4" aria-hidden />
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void onReactivate(p)}
                      aria-label={`${t('actions.reactivate')} ${p.code}`}
                      title={t('actions.reactivate')}
                    >
                      <RotateCcw className="h-4 w-4" aria-hidden />
                    </Button>
                    {/* Hard delete is deactivate-first: only offered once inactive. */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void onPurge(p)}
                      aria-label={`${t('actions.delete')} ${p.code}`}
                      title={t('actions.delete')}
                      className="text-danger hover:text-danger"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  </>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">
            {isVendor ? t('suppliersTitle', { defaultValue: 'Suppliers' }) : side === 'customer' ? t('customersTitle', { defaultValue: 'Customers' }) : t('title')}
          </h1>
          <p className="mt-1 text-sm text-fg-muted">
            {isVendor ? t('suppliersDescription', { defaultValue: 'Your vendors and suppliers.' }) : side === 'customer' ? t('customersDescription', { defaultValue: 'Your customers — with location and who added them.' }) : t('description')}
          </p>
          {/* The tail of a long book is still arriving — say so, or the count
              looks wrong to anyone who knows how many customers they have. */}
          {loadingMore && (
            <p className="mt-1 text-xs text-fg-subtle">
              {/* `n`, not `count` — a `count` interpolation makes i18next look
                  for plural suffixes and fall back to printing the raw key. */}
              {t('list.loadingMore', { n: parties.length, defaultValue: 'Loading the rest of the list… ({{n}} so far)' })}
            </p>
          )}
        </div>
        {canWrite && !showCreate && !editing && (
          <div className="flex flex-wrap items-center gap-2">
            {side === 'customer' && (
              <select
                className="h-9 max-w-[13rem] rounded-md border border-border bg-bg px-2 text-sm text-fg"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                aria-label={t('list.columns.accountType', { defaultValue: 'Account type' })}
              >
                <option value="">{t('list.filterTypeAll', { defaultValue: 'Account type: all' })}</option>
                <option value="normal">{t('list.typeNormal', { defaultValue: 'Normal' })}</option>
                <option value="pending">{t('list.filterTypePending', { defaultValue: 'Pending approval' })}</option>
                {accountTypes.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            )}
            {(side === 'customer' || side === 'vendor') && (
              <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-border bg-bg px-2.5 text-sm text-fg">
                <input
                  type="checkbox"
                  checked={debtorsOnly}
                  onChange={(e) => setDebtorsOnly(e.target.checked)}
                  className="h-4 w-4 accent-[var(--color-brand)]"
                />
                {isVendor ? t('list.filterOwed', { defaultValue: 'With balances' }) : t('list.filterDebtors', { defaultValue: 'With debts' })}
              </label>
            )}
            {showBranch && (
              <select
                className="h-9 max-w-[13rem] rounded-md border border-border bg-bg px-2 text-sm text-fg"
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                aria-label={t('branch.label')}
              >
                <option value="">{t('branch.filterAll')}</option>
                <option value="shared">{t('branch.sharedShort')}</option>
                {branchesInUse.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            )}
            {creators.length > 1 && (
              <select
                className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg"
                value={creatorFilter}
                onChange={(e) => setCreatorFilter(e.target.value)}
                aria-label={t('list.filterByCreator', { defaultValue: 'Added by' })}
              >
                <option value="">{t('list.allCreators', { defaultValue: 'Added by: anyone' })}</option>
                {creators.map(([uid, name]) => (
                  <option key={uid} value={uid}>{name}</option>
                ))}
              </select>
            )}
            {/* No "put customers on a branch" button any more: customers are shared
                (owner, 2026-10-27) and each branch's debt comes from its own invoices. */}
            <Button variant="secondary" onClick={() => setShowImport(true)}>
              {tc('bulkImport.button', { defaultValue: 'Import CSV' })}
            </Button>
            <Button onClick={() => setShowCreate(true)}>{t('create.title')}</Button>
          </div>
        )}
      </header>

      {showImport && (
        <BulkImportDialog
          title={tc('bulkImport.titleParties', { defaultValue: 'Import customers & vendors' })}
          endpoint="/parties/bulk-import"
          templateCsv={PARTIES_CSV_TEMPLATE}
          templateName="parties-template.csv"
          onDone={() => { setShowImport(false); void reload(); }}
          onCancel={() => setShowImport(false)}
        />
      )}

      {showCreate && (
        <PartyForm
          mode="create"
          side={side}
          onCancel={() => setShowCreate(false)}
          onSaved={async () => {
            setShowCreate(false);
            await reload();
          }}
        />
      )}

      {editing && (
        <PartyForm
          mode="edit"
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      )}

      {parties.length === 0 && !showCreate ? (
        <EmptyState
          icon={<Users className="h-8 w-8" aria-hidden />}
          title={t('list.empty')}
          description={t('list.emptyDescription')}
          action={
            canWrite ? (
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button onClick={() => setShowCreate(true)}>{t('create.title')}</Button>
                <Button variant="secondary" onClick={() => setShowImport(true)}>
                  {tc('bulkImport.button', { defaultValue: 'Import CSV' })}
                </Button>
              </div>
            ) : undefined
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={visible}
            rowKey={(p) => p.id}
            searchPlaceholder={t('searchPlaceholder')}
            toolbar={({ rows, query }) => (
              // Exactly what the table shows — filters, search and sort, every
              // page. Held back while the rest of the book is still arriving,
              // or the file would quietly stop at the names loaded so far.
              <>
                <Button
                  variant="secondary"
                  onClick={() => exportCsv(rows)}
                  disabled={loadingMore || rows.length === 0}
                  title={loadingMore ? t('listPrint.waitLoading', { defaultValue: 'Still loading the list…' }) : undefined}
                >
                  <Download className="me-1.5 h-4 w-4" aria-hidden />
                  {t('listPrint.exportCsv', { defaultValue: 'Export CSV' })}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setPrintJob({ key: Date.now(), rows, filters: filterLabels(query) })}
                  disabled={loadingMore || rows.length === 0}
                  title={loadingMore ? t('listPrint.waitLoading', { defaultValue: 'Still loading the list…' }) : undefined}
                >
                  <Printer className="me-1.5 h-4 w-4" aria-hidden />
                  {t('listPrint.print', { defaultValue: 'Print' })}
                </Button>
              </>
            )}
          />
        </Card>
      )}

      {printJob && (
        <PartiesListPrint
          key={printJob.key}
          rows={printJob.rows}
          side={side}
          filters={printJob.filters}
          showBranch={showBranch}
          onDone={() => setPrintJob(null)}
        />
      )}
    </div>
  );
}

interface PartyFormProps {
  mode: 'create' | 'edit';
  /** Required when mode === 'edit'; populates the form and supplies the id for PATCH. */
  initial?: Party;
  /** Customers vs suppliers list — sets the default + offered party types. */
  side?: 'customer' | 'vendor';
  /** Hide the type selector + lock to `side` (e.g. the POS always adds a customer). */
  hideType?: boolean;
  onCancel: () => void;
  /** Receives the created (or edited) party so callers can use its id. */
  onSaved: (party?: Party) => void | Promise<void>;
}

export function PartyForm({ mode, initial, side, hideType, onCancel, onSaved }: PartyFormProps): JSX.Element {
  const { t } = useTranslation('parties');
  const { t: tc } = useTranslation();
  const isEdit = mode === 'edit';
  const allowedTypes: PartyType[] = side ? [side, 'both'] : PARTY_TYPES;
  const [form, setForm] = useState<PartyCreate>(() =>
    initial
      ? {
          code: initial.code,
          displayName: initial.displayName,
          partyType: initial.partyType,
          email: initial.email ?? undefined,
          phone: initial.phone ?? undefined,
          taxId: initial.taxId ?? undefined,
          paymentTermsDays: initial.paymentTermsDays,
          latitude: initial.latitude ?? undefined,
          longitude: initial.longitude ?? undefined,
          priceListId: initial.priceListId ?? undefined,
        }
      : {
          code: '',
          displayName: '',
          partyType: side ?? 'customer',
          paymentTermsDays: 30,
        },
  );
  // Location is a customer concept — hide the map on the suppliers form.
  const showMap = side !== 'vendor';
  // Price lists apply to customers; only offer the selector when the module is on.
  const tenantId = useAuthStore((s) => s.tenant?.id ?? null);
  const pricingOn = moduleEnabled(useEnabledModules(tenantId), 'pricing') && side !== 'vendor';
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  useEffect(() => {
    if (pricingOn) void listPriceLists().then((l) => setPriceLists(l.filter((x) => x.isActive))).catch(() => {});
  }, [pricingOn]);
  const coords = form.latitude != null && form.longitude != null ? { lat: form.latitude, lng: form.longitude } : null;
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Home branch. Customers are SHARED (owner, 2026-10-27): every branch sees
  // them, and each keeps its own debt through its own invoices. Only the owner
  // may still file one on a branch on purpose; branch staff never see the field.
  const { branches, allowed, loaded: branchesLoaded } = useBranchScope();
  const isSupplier = (isEdit ? initial?.partyType === 'vendor' : form.partyType === 'vendor') || side === 'vendor';
  const branchField: 'owner' | null = branchesLoaded && !isSupplier && allowed === null && branches.length > 0 ? 'owner' : null;
  const [branchId, setBranchId] = useState(initial?.warehouseId ?? '');
  /** The customer who already has this phone number — offered instead of a second copy. */
  const [phoneOwner, setPhoneOwner] = useState<{ id: string; code: string; name: string } | null>(null);

  /** Use the existing customer: the caller treats it as the one just saved (the register selects it). */
  const pickExisting = async (): Promise<void> => {
    if (!phoneOwner) return;
    setSaving(true);
    try {
      const r = await api().get<Party>(`/parties/${phoneOwner.id}`);
      await onSaved(r.data);
    } catch {
      setError(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setPhoneOwner(null);
    setSaving(true);
    try {
      if (isEdit && initial) {
        // Code is the unique identifier and is intentionally read-only
        // in edit mode — PartyUpdateSchema doesn't accept it. Send the
        // explicitly editable fields; empty strings on optional text
        // fields collapse to null so the server clears them.
        const patch: Record<string, unknown> = {
          displayName: form.displayName.trim(),
          partyType: form.partyType,
          email: form.email && form.email.trim() ? form.email.trim() : null,
          phone: form.phone && form.phone.trim() ? form.phone.trim() : null,
          taxId: form.taxId && form.taxId.trim() ? form.taxId.trim() : null,
          paymentTermsDays: form.paymentTermsDays,
          latitude: form.latitude ?? null,
          longitude: form.longitude ?? null,
          ...(pricingOn ? { priceListId: form.priceListId ?? null } : {}),
          ...(branchField === 'owner' ? { warehouseId: branchId || null } : {}),
        };
        await api().patch(`/parties/${initial.id}`, patch);
        await onSaved(initial);
      } else {
        const payload: PartyCreate = {
          ...form,
          code: form.code.trim(),
          displayName: form.displayName.trim(),
          ...(branchField ? { warehouseId: branchId || null } : {}),
        };
        if (!payload.email) delete payload.email;
        if (!payload.phone) delete payload.phone;
        if (!payload.taxId) delete payload.taxId;
        const created = await api().post<Party>('/parties', payload);
        await onSaved(created.data);
      }
    } catch (err) {
      const res = (err as { response?: { status?: number; data?: { code?: string; cause?: { code?: string; existingId?: string; existingCode?: string; existingName?: string } } } }).response;
      const code = res?.data?.code;
      const cause = res?.data?.cause;
      if (cause?.code === 'phone_taken' && cause.existingId) {
        // One phone, one customer: name the one that has it, and offer it.
        setPhoneOwner({ id: cause.existingId, code: cause.existingCode ?? '', name: cause.existingName ?? '' });
        setError(t('errors.phoneTaken', {
          name: cause.existingName ?? '', code: cause.existingCode ?? '',
          defaultValue: 'This phone number already belongs to «{{name}}» ({{code}}).',
        }));
      } else if (cause?.code === 'warehouse_required') setError(t('errors.branchRequired'));
      else if (code === 'conflict') setError(t('errors.duplicateCode'));
      else if (code === 'unprocessable') setError(t('errors.unprocessable'));
      // A refusal is not a fault. This said "a server error occurred, try
      // again" to anybody without `parties.write` — so they tried again, and
      // again, and reported a broken server. The form was working exactly as
      // configured; only the message was wrong.
      else if (res?.status === 403) setError(t('errors.noPermission'));
      else setError(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  const title = isEdit && initial
    ? t('edit.title', { code: initial.code, name: initial.displayName })
    : t('create.title');

  return (
    <Card>
      <h2 className="mb-4 text-lg font-semibold">{title}</h2>
      <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={onSubmit} noValidate>
        <FormField id="p-code" label={t('create.code')} required>
          <Input
            id="p-code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            required
            autoFocus={!isEdit}
            readOnly={isEdit}
            aria-readonly={isEdit}
            className={isEdit ? 'cursor-not-allowed bg-bg-muted text-fg-muted' : undefined}
          />
        </FormField>
        <FormField id="p-name" label={t('create.name')} required>
          <Input id="p-name" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} required />
        </FormField>
        {!hideType && (
          <FormField id="p-type" label={t('create.type')} required>
            <select
              id="p-type"
              className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
              value={form.partyType}
              onChange={(e) => setForm({ ...form, partyType: e.target.value as PartyType })}
            >
              {allowedTypes.map((type) => (
                <option key={type} value={type}>
                  {t(`type.${type}`)}
                </option>
              ))}
            </select>
          </FormField>
        )}
        {/* Customers are phone-identified — email exists only for vendor/AP contacts. */}
        {form.partyType !== 'customer' && (
          <FormField id="p-email" label={t('create.email')}>
            <Input id="p-email" type="email" value={form.email ?? ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </FormField>
        )}
        <FormField id="p-phone" label={t('create.phone')}>
          <PhoneInput id="p-phone" value={form.phone ?? ''} onChange={(v) => setForm({ ...form, phone: v })} />
        </FormField>
        <FormField id="p-terms" label={t('create.paymentTermsDays')}>
          <Input
            id="p-terms"
            type="number"
            inputMode="numeric"
            min={0}
            max={365}
            value={form.paymentTermsDays}
            onChange={(e) => setForm({ ...form, paymentTermsDays: Number(e.target.value) || 0 })}
          />
        </FormField>
        <FormField id="p-tax" label={t('create.taxId')}>
          <Input id="p-tax" value={form.taxId ?? ''} onChange={(e) => setForm({ ...form, taxId: e.target.value })} />
        </FormField>
        {branchField && (
          <FormField
            id="p-branch"
            label={t('branch.label')}
            helperText={t('branch.help')}
          >
            <select
              id="p-branch"
              className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
            >
              <option value="">{t('branch.shared')}</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </FormField>
        )}
        {pricingOn && (
          <FormField id="p-pricelist" label={tc('pricing:assign.label', { defaultValue: 'Price list' })} helperText={tc('pricing:assign.hint', { defaultValue: '' })}>
            <select
              id="p-pricelist"
              className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
              value={form.priceListId ?? ''}
              onChange={(e) => setForm({ ...form, priceListId: e.target.value || undefined })}
            >
              <option value="">{tc('pricing:assign.none', { defaultValue: 'None (base prices)' })}</option>
              {priceLists.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </FormField>
        )}
        {showMap && (
          <div className="md:col-span-2 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-fg">{t('create.location', { defaultValue: 'Location' })}</label>
              {coords ? (
                <span className="font-mono text-xs text-fg-muted">
                  {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, latitude: undefined, longitude: undefined })}
                    className="ms-2 text-danger hover:underline"
                  >
                    {tc('actions.remove', { defaultValue: 'Remove' })}
                  </button>
                </span>
              ) : (
                <span className="text-xs text-fg-muted">{t('create.locationHint', { defaultValue: 'Search, click the map, or drag the pin' })}</span>
              )}
            </div>
            <MapPicker
              value={coords}
              onChange={(v) => setForm({ ...form, latitude: v.lat, longitude: v.lng })}
              searchPlaceholder={t('create.locationSearch', { defaultValue: 'Search an address or place…' })}
              unavailableLabel={t('create.mapUnavailable', { defaultValue: 'Map unavailable (offline).' })}
            />
          </div>
        )}
        {error && (
          <div className="md:col-span-2 flex flex-wrap items-center gap-2" role="alert">
            <p className="text-sm text-danger">{error}</p>
            {phoneOwner && (
              <Button type="button" size="sm" variant="secondary" loading={saving} onClick={() => void pickExisting()}>
                {t('errors.useExisting', { name: phoneOwner.name, defaultValue: 'Use «{{name}}»' })}
              </Button>
            )}
          </div>
        )}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{isEdit ? t('edit.submit') : t('create.submit')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{isEdit ? t('edit.cancel') : t('create.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}
